import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useConfiguracion from '../../hooks/useConfiguracion';
import ModalCambiarPassword from '../../components/Panel/ModalCambiarPassword';
import { RoutesPanel } from '../../Security/ProtectedRoute';
import '../../styles/Panel.css';

const SIDEBAR_COLAPSADO_KEY = 'panel_sidebar_colapsado';

// Menú agrupado (HU-009-D) — antes era una lista plana de 7 items sin
// ningún criterio de orden visible. "Operaciones" junta la atención diaria
// (tickets/vías/categorías, lo que pidió el cliente explícitamente agrupar);
// "Análisis" queda separado para cuando sume mapas de calor/comparativas
// (fuera de alcance de esta fase, solo tiene Reportes por ahora);
// "Administración" son las pantallas de gestión de la instancia en sí.
// `titulo: null` es el grupo sin encabezado (Alertas del día, la portada).
const GRUPOS_NAV = [
  {
    titulo: null,
    items: [
      { ruta: RoutesPanel.INICIO, icono: 'fa-gauge-high', etiqueta: 'Dashboard', soloStaffAdmin: true },
    ],
  },
  {
    titulo: 'Operaciones',
    items: [
      { ruta: '/panel/tickets', icono: 'fa-triangle-exclamation', etiqueta: 'Incidencias', soloStaffAdmin: true },
      { ruta: '/panel/vias', icono: 'fa-road', etiqueta: 'Vías', soloStaffAdmin: true },
      { ruta: '/panel/categorias', icono: 'fa-tags', etiqueta: 'Categorías', soloStaffAdmin: true },
      { ruta: '/panel/comentarios', icono: 'fa-comments', etiqueta: 'Comentarios', soloStaffAdmin: true },
    ],
  },
  {
    titulo: 'Análisis',
    items: [
      { ruta: '/panel/reportes', icono: 'fa-chart-column', etiqueta: 'Reportes', soloStaffAdmin: true },
    ],
  },
  {
    titulo: 'Administración',
    items: [
      { ruta: '/panel/usuarios', icono: 'fa-users', etiqueta: 'Usuarios', soloStaffAdmin: true },
      { ruta: '/panel/roles', icono: 'fa-user-shield', etiqueta: 'Roles y permisos', soloStaffAdmin: true },
      { ruta: '/panel/bloqueos', icono: 'fa-ban', etiqueta: 'Bloqueos IP', soloStaffAdmin: true },
      { ruta: '/panel/configuracion', icono: 'fa-gear', etiqueta: 'Configuración', soloStaffAdmin: true },
    ],
  },
];

const SECCIONES = GRUPOS_NAV.flatMap((g) => g.items);

const FORMATO_FECHA = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };

const iniciales = (nombreCompleto) => {
  if (!nombreCompleto) return '--';
  const partes = nombreCompleto.trim().split(/\s+/);
  return ((partes[0]?.[0] || '') + (partes[1]?.[0] || '')).toUpperCase();
};

/**
 * Shell del panel privado — rediseño fiel a
 * design_handoff_reportes_ciudadanos sección 7.2: sidebar 238px/72px
 * colapsado sobre `--ink`, navbar 66px con avatar de iniciales y rol en
 * mono, footer. El estado de colapsado se guarda en `localStorage` para que
 * no se resetee al navegar entre pantallas (cada una remonta este shell).
 * Las entradas marcadas `soloStaffAdmin` solo se listan para
 * `ADMINISTRADOR`/`SUPERADMIN` — no es el guard real (eso lo hace
 * `RutaProtegidaPanel` en `PanelRoutes`), es solo para no mostrar un link a
 * una pantalla a la que ese usuario no puede entrar. Reemplazo pendiente
 * (HU-004 Fase 4): esta lista pasa a filtrarse por permiso real
 * (`usePermisos`), no por este flag fijo.
 */
const PanelShell = ({ children }) => {
  const { user, logout } = useAuth();
  const { configuracion } = useConfiguracion();
  const navigate = useNavigate();
  const location = useLocation();

  const [colapsado, setColapsado] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLAPSADO_KEY) === 'true';
    } catch {
      return false;
    }
  });
  // Menú mobile (HU-011): en desktop el sidebar es la barra fija de siempre
  // (colapsable a 72px); por debajo del breakpoint mobile pasa a ser un
  // cajón oculto que se abre con el botón hamburguesa del header y se
  // cierra solo, tanto al tocar el fondo oscurecido como al navegar — antes
  // se forzaba siempre a 72px fijo en mobile, comiéndose casi un cuarto de
  // una pantalla de teléfono de forma permanente.
  const [menuMovilAbierto, setMenuMovilAbierto] = useState(false);
  const [modalPasswordAbierto, setModalPasswordAbierto] = useState(false);

  const esStaffAdmin = ['ADMINISTRADOR', 'SUPERADMIN'].includes(user?.usu_chRol);
  const gruposVisibles = GRUPOS_NAV
    .map((g) => ({ ...g, items: g.items.filter((s) => !s.soloStaffAdmin || esStaffAdmin) }))
    .filter((g) => g.items.length > 0);
  // Coincidencia por prefijo (salvo la raíz "/panel") para tolerar subrutas
  // futuras sin perder el resaltado de la sección activa.
  const seccionActiva = SECCIONES.find((s) => (
    s.ruta === RoutesPanel.INICIO ? location.pathname === s.ruta : location.pathname.startsWith(s.ruta)
  ));
  const nombreCompleto = `${user?.usu_chNombres || ''} ${user?.usu_chApePat || ''}`.trim();
  const fechaCruda = new Date().toLocaleDateString('es-PE', FORMATO_FECHA);
  const fechaHoy = fechaCruda.charAt(0).toUpperCase() + fechaCruda.slice(1);

  const toggleColapsado = () => {
    setColapsado((actual) => {
      const nuevo = !actual;
      try {
        localStorage.setItem(SIDEBAR_COLAPSADO_KEY, String(nuevo));
      } catch {
        // localStorage puede fallar (modo privado, cuota) — el colapso
        // simplemente no persiste entre pantallas, no rompe nada más.
      }
      return nuevo;
    });
  };

  const handleLogout = async () => {
    const resultado = await logout();
    if (resultado.success) {
      navigate(RoutesPanel.LOGIN, { replace: true });
    }
    // `resultado.cancelado`: el usuario canceló el diálogo de confirmación
    // — se queda donde estaba, sin tocar nada.
  };

  return (
    <div className={`panel-shell ${colapsado ? 'panel-shell-colapsado' : ''} ${menuMovilAbierto ? 'panel-shell-menu-abierto' : ''}`}>
      {menuMovilAbierto && (
        <div className="panel-sidebar-backdrop" onClick={() => setMenuMovilAbierto(false)}></div>
      )}
      <aside className="panel-sidebar">
        <div className="panel-sidebar-cabecera">
          <div className="panel-sidebar-marca">
            <span className="panel-sidebar-titulo">{configuracion.nombre_entidad}</span>
            <span className="panel-sidebar-consola">CONSOLA</span>
          </div>
          <button
            type="button"
            className="panel-sidebar-toggle"
            onClick={toggleColapsado}
            aria-label={colapsado ? 'Expandir menú' : 'Colapsar menú'}
          >
            <i className={`fas ${colapsado ? 'fa-angles-right' : 'fa-angles-left'}`}></i>
          </button>
          <button
            type="button"
            className="panel-sidebar-cerrar-movil"
            onClick={() => setMenuMovilAbierto(false)}
            aria-label="Cerrar menú"
          >
            <i className="fas fa-xmark"></i>
          </button>
        </div>
        <nav className="panel-sidebar-nav">
          {gruposVisibles.map((grupo) => (
            <div key={grupo.titulo || 'sin-grupo'} className="panel-sidebar-grupo">
              {grupo.titulo && !colapsado && (
                <span className="panel-sidebar-grupo-titulo">{grupo.titulo}</span>
              )}
              {grupo.items.map((s) => (
                <Link
                  key={s.ruta}
                  to={s.ruta}
                  className={`panel-sidebar-link ${s === seccionActiva ? 'panel-sidebar-link-activo' : ''}`}
                  title={colapsado ? s.etiqueta : undefined}
                  onClick={() => setMenuMovilAbierto(false)}
                >
                  <i className={`fas ${s.icono}`}></i>
                  <span>{s.etiqueta}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className="panel-area-principal">
        <header className="panel-header">
          <button
            type="button"
            className="panel-header-hamburguesa"
            onClick={() => setMenuMovilAbierto(true)}
            aria-label="Abrir menú"
          >
            <i className="fas fa-bars"></i>
          </button>
          <span className="panel-header-titulo">{seccionActiva?.etiqueta || 'Panel'}</span>
          {!colapsado && <span className="panel-header-fecha">{fechaHoy}</span>}
          <div className="panel-header-sesion">
            <div className="panel-header-avatar">{iniciales(nombreCompleto)}</div>
            <div className="panel-header-identidad">
              <span className="panel-header-usuario">{nombreCompleto || 'Personal municipal'}</span>
              <span className="panel-header-rol">{user?.usu_chRol}</span>
            </div>
            <button type="button" className="panel-header-cambiar-password" onClick={() => setModalPasswordAbierto(true)} title="Cambiar mi contraseña">
              <i className="fas fa-key"></i>
            </button>
            <button type="button" className="panel-header-salir" onClick={handleLogout}>
              Salir
            </button>
          </div>
        </header>

        <main className="panel-contenido">{children}</main>

        <footer className="panel-footer">
          <span>{configuracion.nombre_entidad} — Panel de administración</span>
        </footer>
      </div>

      {modalPasswordAbierto && <ModalCambiarPassword onCerrar={() => setModalPasswordAbierto(false)} />}
    </div>
  );
};

export default PanelShell;
